import React, { useState, useMemo, useEffect } from "react";
import RotationDisplay from "./RotationDisplay";
import { useTheme } from "../config/ThemeContext"; 

function formatTime(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  } else {
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
}


const RotationPanel = ({
  visible = false,
  hidden = false,
  onToggle = () => {},
  rotationHistory = [],
  playerMapA = {},
  playerMapB = {},
  teamAName = "Team A",
  teamBName = "Team B"
}) => {
  const [pageIndex, setPageIndex] = useState(0);
    const theme = useTheme();


  const groupedByTime = useMemo(() => {
    const grouped = {};
    for (const entry of rotationHistory) {
      if (!grouped[entry.matchTimeElapsed]) grouped[entry.matchTimeElapsed] = [];
      grouped[entry.matchTimeElapsed].push(entry);
    }
    return Object.entries(grouped)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([time, entries]) => ({ matchTimeElapsed: Number(time), entries }));
  }, [rotationHistory]);

  const currentPage = groupedByTime[pageIndex] || { entries: [] };

  const latestRotationA = useMemo(() => {
    let rot = null;
    for (let i = 0; i <= pageIndex; i++) {
      const e = groupedByTime[i]?.entries.find(e => e.team === "teamA");
      if (e) rot = e.rotation;
    }
    return rot || [];
  }, [groupedByTime, pageIndex]);

  const latestRotationB = useMemo(() => {
    let rot = null;
    for (let i = 0; i <= pageIndex; i++) {
      const e = groupedByTime[i]?.entries.find(e => e.team === "teamB");
      if (e) rot = e.rotation;
    }
    return rot || [];
  }, [groupedByTime, pageIndex]);

  useEffect(() => {
    if (rotationHistory.length === 0) return;
    const uniqueTimes = [...new Set(rotationHistory.map(r => r.matchTimeElapsed))];
    const latestIndex = uniqueTimes.length - 1;
    setPageIndex(latestIndex);
  }, [rotationHistory]);

  return (
    <div className={`p-4 rounded-md shadow ${theme.neutral.bg} border ${theme.neutral.border}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-semibold ${theme.primary.text}`}>Rotation History</h3>
        <button
          className={`text-sm px-2 py-1 rounded ${theme.primary.text} ${theme.neutral.hover}`}
          onClick={onToggle}
        >
          {!hidden ? "🚫 Hide" : "📜 Show"}
        </button>
      </div>

      {visible && (
        <div className={`space-y-4 transition-all duration-300 ${hidden ? "hidden" : "block animate-fade-in"}`}>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mb-2">
            <button
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              disabled={pageIndex === 0}
              className={`px-3 py-1 rounded border ${theme.primary.border} text-sm ${
                pageIndex === 0
                  ? "opacity-40 cursor-not-allowed"
                  : `${theme.primary.text} ${theme.primary.hover}`
              }`}
            >
              ⬅ Prev
            </button>

            <span className={`text-sm font-medium ${theme.primary.text}`}>
              🕒 {formatTime(currentPage.matchTimeElapsed ?? 0)}
            </span>

            <button
              onClick={() =>
                setPageIndex((p) => Math.min(groupedByTime.length - 1, p + 1))
              }
              disabled={pageIndex >= groupedByTime.length - 1}
              className={`px-3 py-1 rounded border ${theme.primary.border} text-sm ${
                pageIndex >= groupedByTime.length - 1
                  ? "opacity-40 cursor-not-allowed"
                  : `${theme.primary.text} ${theme.primary.hover}`
              }`}
            >
              Next ➡
            </button>
          </div>

          {/* Rotation Display Row */}
          <div className="flex justify-center items-start gap-12">
            <RotationDisplay
              teamName={teamAName}
              rotation={latestRotationA}
              playerMap={playerMapA}
			  team="teamA"
			  showRotationControls={false}

            />
            <RotationDisplay
              teamName={teamBName}
              rotation={latestRotationB}
              playerMap={playerMapB}
			  team="teamB"
			  showRotationControls={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default RotationPanel;